/**
 * Opportunity Matching Component
 * Shows SEU data from SEU module, matches to opportunities, calculates & ranks
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Zap, AlertCircle, CheckCircle, ChevronDown, ChevronRight,
  Brain, Calculator, Loader2, RefreshCw, Sparkles, Target, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CONFIDENCE_COLORS = {
  High: 'bg-green-100 text-green-800 border-green-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-red-100 text-red-800 border-red-200'
};

const OpportunityMatchingV3 = ({ study, masterDataset, onRefresh, onViewPortfolio }) => {
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);
  const [expandedSeus, setExpandedSeus] = useState([]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (study?.id) {
      fetchMatches();
    }
  }, [study?.id]);

  const fetchMatches = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/opportunity-v3/matching/study/${study.id}/match-opportunities`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatches(res.data.matches || []);
      setWarning(res.data.sector_filter_warning || null);

      if (res.data.matches?.length > 0) {
        setExpandedSeus(res.data.matches.slice(0, 3).map(m => m.seu_id));
      }

      // Load previously saved AI analysis
      if (res.data.ai_analysis) {
        setAiAnalysis(res.data.ai_analysis);
      }
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAiAnalysis = async () => {
    setAiLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(
        `${API}/api/opportunity-v3/matching/study/${study.id}/ai-analysis`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiAnalysis(res.data.analysis);
    } catch (error) {
      console.error('Error running AI analysis:', error);
      alert('Error running AI analysis: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiLoading(false);
    }
  };

  const runCalculation = async () => {
    setCalculating(true);
    const token = localStorage.getItem('token');
    try {
      // Just trigger calculation and navigate to portfolio
      await axios.post(
        `${API}/api/opportunity-v3/matching/study/${study.id}/calculate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Navigate to portfolio view
      if (onViewPortfolio) {
        onViewPortfolio();
      }
    } catch (error) {
      console.error('Error calculating:', error);
      alert('Error calculating: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCalculating(false);
    }
  };

  const toggleSeu = (seuId) => {
    if (expandedSeus.includes(seuId)) {
      setExpandedSeus(expandedSeus.filter(id => id !== seuId));
    } else {
      setExpandedSeus([...expandedSeus, seuId]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading SEU data and matching opportunities...</p>
        </div>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">No SEU Data Available</h3>
        <p className="text-gray-600 mb-4">
          Complete the SEU Identification module first to match opportunities.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sector-filter warning — explains an empty/blank result (fail-closed) */}
      {warning && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{warning}</p>
        </div>
      )}

      {/* Header Stats */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-indigo-600" />
              <span className="font-semibold text-gray-900">{matches.length} SEUs</span>
              <span className="text-gray-500">matched</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-gray-500">Data from SEU Module</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={runAiAnalysis}
              disabled={aiLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 text-sm"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              AI Analysis
            </button>
            
            <button
              onClick={runCalculation}
              disabled={calculating}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
              data-testid="calculate-btn"
            >
              {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Calculate & Rank
            </button>
            
            <button
              onClick={fetchMatches}
              className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* AI Analysis Results */}
      {aiAnalysis && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-purple-900 mb-2">AI Analysis Results</h4>
              
              {aiAnalysis.summary && (
                <p className="text-sm text-purple-800 mb-3">{aiAnalysis.summary}</p>
              )}
              
              {aiAnalysis.priority_actions && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-purple-700 mb-1">Priority Actions:</p>
                  <ul className="text-sm text-purple-800 space-y-1">
                    {aiAnalysis.priority_actions.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-purple-500">→</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SEU Matches */}
      <div className="space-y-3">
        {matches.map((match) => {
          const isExpanded = expandedSeus.includes(match.seu_id);
          const existingData = match.existing_data || {};
          
          return (
            <div key={match.seu_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* SEU Header */}
              <button
                onClick={() => toggleSeu(match.seu_id)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <div className="text-left">
                    <div className="font-semibold text-gray-900">{match.equipment_name || match.equipment_type}</div>
                    <div className="text-xs text-gray-500">{match.seu_code} • {match.seu_type_category?.split(' ')[0]}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {match.observation_types?.length > 0 && (
                    <div className="flex gap-1">
                      {match.observation_types.slice(0, 2).map((obs, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                          {obs}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs border ${CONFIDENCE_COLORS[match.confidence]}`}>
                    {match.confidence}
                  </span>
                  <span className="text-sm text-gray-600">
                    {match.matched_opportunities?.length || 0} opps
                  </span>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-100">
                  {/* Existing SEU Data — resource-aware. Electrical uses the
                       kW/load/hrs shape; fuel/water/waste show their own
                       physical quantities instead of blank "-" placeholders. */}
                  <div className="px-4 py-3 bg-green-50 border-b border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-900">Data from SEU Module</span>
                    </div>
                    {(() => {
                      const rt = (existingData.resource_type || match.resource_type || '').toLowerCase();
                      const fmt = (v) => (v === null || v === undefined || v === '') ? '-' : v;
                      const num = (v) => (v === null || v === undefined || v === '') ? '-' : Number(v).toLocaleString();
                      if (rt === 'fuel') {
                        const unit = existingData.unit_label || 'units';
                        return (
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div><span className="text-gray-500">Fuel Type:</span><span className="ml-2 font-medium">{fmt(existingData.fuel_type)}</span></div>
                            <div><span className="text-gray-500">Monthly Qty:</span><span className="ml-2 font-medium">{num(existingData.monthly_qty)} {unit}</span></div>
                            <div><span className="text-gray-500">Annual Qty:</span><span className="ml-2 font-medium">{num(existingData.annual_qty)} {unit}</span></div>
                            <div><span className="text-gray-500">Annual Energy:</span><span className="ml-2 font-medium">{num(existingData.annual_energy)} GJ</span></div>
                          </div>
                        );
                      }
                      if (rt === 'water') {
                        return (
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div><span className="text-gray-500">Source:</span><span className="ml-2 font-medium">{fmt(existingData.source_or_category)}</span></div>
                            <div><span className="text-gray-500">Daily:</span><span className="ml-2 font-medium">{num(existingData.daily_kl)} KL</span></div>
                            <div><span className="text-gray-500">Monthly:</span><span className="ml-2 font-medium">{num(existingData.monthly_qty)} KL</span></div>
                            <div><span className="text-gray-500">Annual:</span><span className="ml-2 font-medium">{num(existingData.annual_qty)} KL</span></div>
                          </div>
                        );
                      }
                      if (rt === 'waste') {
                        const unit = existingData.unit_label || 'MT';
                        return (
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div><span className="text-gray-500">Waste Type:</span><span className="ml-2 font-medium">{fmt(existingData.fuel_type)}</span></div>
                            <div><span className="text-gray-500">Category:</span><span className="ml-2 font-medium">{fmt(existingData.source_or_category)}</span></div>
                            <div><span className="text-gray-500">Monthly:</span><span className="ml-2 font-medium">{num(existingData.monthly_qty)} {unit}</span></div>
                            <div><span className="text-gray-500">Disposal:</span><span className="ml-2 font-medium">{fmt(existingData.disposal_method)}</span></div>
                          </div>
                        );
                      }
                      // Electrical (default)
                      return (
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div><span className="text-gray-500">Rated Capacity:</span><span className="ml-2 font-medium">{fmt(existingData.rated_capacity)} {existingData.rated_capacity_unit || 'kW'}</span></div>
                          <div><span className="text-gray-500">Load:</span><span className="ml-2 font-medium">{fmt(existingData.load_percent)}%</span></div>
                          <div><span className="text-gray-500">Hours/Day:</span><span className="ml-2 font-medium">{fmt(existingData.hours_per_day)} hrs</span></div>
                          <div><span className="text-gray-500">Annual Energy:</span><span className="ml-2 font-medium">{num(existingData.annual_energy)} kWh</span></div>
                        </div>
                      );
                    })()}
                    {match.identification_reason && (
                      <div className="mt-2 text-xs text-green-700">
                        <span className="font-medium">Why SEU:</span> {match.identification_reason}
                      </div>
                    )}
                  </div>

                  {/* Matched Opportunities */}
                  {match.matched_opportunities?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Opportunity</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">CAPEX Item</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Technical Spec</th>
                            <th className="px-4 py-2 text-right font-semibold text-gray-600">Unit Cost</th>
                            <th className="px-4 py-2 text-left font-semibold text-gray-600">Basis</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {match.matched_opportunities.slice(0, 5).map((opp, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium text-gray-900">{opp.opportunity_name}</td>
                              <td className="px-4 py-2 text-gray-700">{opp.capex_item_name}</td>
                              <td className="px-4 py-2 text-gray-600 text-xs">{opp.technical_spec}</td>
                              <td className="px-4 py-2 text-right text-gray-900">
                                {opp.unit_cost_low?.toLocaleString()} - {opp.unit_cost_high?.toLocaleString()}
                              </td>
                              <td className="px-4 py-2 text-gray-600">{opp.capacity_basis}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {match.matched_opportunities.length > 5 && (
                        <div className="px-4 py-2 text-sm text-gray-500 text-center">
                          + {match.matched_opportunities.length - 5} more opportunities
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Calculate CTA */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-6 h-6 text-indigo-600" />
            <div>
              <p className="font-semibold text-indigo-900">Ready to Calculate?</p>
              <p className="text-sm text-indigo-700">
                Click "Calculate & Rank" to estimate CAPEX, savings, and payback for all matched opportunities.
              </p>
            </div>
          </div>
          <button
            onClick={runCalculation}
            disabled={calculating}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2 font-medium"
          >
            {calculating ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            Calculate & Rank
          </button>
        </div>
      </div>
    </div>
  );
};

export default OpportunityMatchingV3;
